import {
    getDocs,
    limit,
    orderBy,
    query,
    Timestamp,
    where,
} from "firebase/firestore";
import { RepositoryFirebase } from "../common/repository.firebase";
import { Sell, SellPaymentMethod } from "./sell.entity";

class SellRepository extends RepositoryFirebase<Sell> {
    static readonly instance = new SellRepository();

    protected collection = 'sells';
    protected entity = Sell;

    private getAllUnpaidQuery() {
        const ref = this.getCollection();
        return query(ref, where("paymentMethod", "==", SellPaymentMethod.UNPAID));
    }

    async getAllUnpaid() {
        const qSnapshot = await getDocs(this.getAllUnpaidQuery());

        return await Promise.all(qSnapshot.docs.map(async(sell) => {
            return await Sell.build(sell.id, sell.data());
        }));
    }

    /**
     * Vendas concluídas (com pagamento), ordenadas da mais recente.
     * Usa orderBy em completedAt; documentos sem data ficam fora ou ao fim da ordenação.
     */
    async getRecentCompletedSells(maxCount = 500) {
        const ref = this.getCollection();
        const q = query(ref, orderBy("completedAt", "desc"), limit(maxCount));
        const qSnapshot = await getDocs(q);
        const items = await Promise.all(
            qSnapshot.docs.map(async (sell) => Sell.build(sell.id, sell.data()))
        );
        return items.filter(
            (s) =>
                s.getCompletedAt() &&
                s.getPaymentMethod() !== SellPaymentMethod.UNPAID
        );
    }

    /**
     * Vendas concluídas com `completedAt` entre `start` e `end` (inclusive), mais recentes primeiro.
     */
    async getCompletedSellsInDateRange(
        start: Date,
        end: Date,
        maxCount = 10000
    ) {
        const ref = this.getCollection();
        const q = query(
            ref,
            where("completedAt", ">=", Timestamp.fromDate(start)),
            where("completedAt", "<=", Timestamp.fromDate(end)),
            orderBy("completedAt", "desc"),
            limit(maxCount)
        );
        const qSnapshot = await getDocs(q);
        const items = await Promise.all(
            qSnapshot.docs.map(async (sell) => Sell.build(sell.id, sell.data()))
        );
        return items.filter(
            (s) =>
                s.getCompletedAt() &&
                s.getPaymentMethod() !== SellPaymentMethod.UNPAID
        );
    }

    useAllUnpaidState() {
        return this.useSubscribeQuery(this.getAllUnpaidQuery());
    }

    /** Todas as vendas vinculadas à sessão de caixa (inclui em aberto). */
    async getSellsByCashSessionId(sessionId: string) {
        const ref = this.getCollection();
        const q = query(ref, where("cashSessionId", "==", sessionId));
        const snap = await getDocs(q);
        return await Promise.all(
            snap.docs.map(async (d) => Sell.build(d.id, d.data()))
        );
    }
}

export const sellRepository = SellRepository.instance;